
<script>
export default {
  name: 'ModalFindingAction',

  props: {
    item: {
      type: Object,
      required: true,
    },
    mapComponent: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    findingActionGroups() {
      const details = this.item?.tasks_checklist_detail || []
      const grouped = details.reduce((acc, detail) => {
        const key = [
          detail.component_line_id || 'line',
          detail.reserved_product_id || 'product',
          detail.description || 'component',
        ].join('-')

        if (!acc[key]) {
          acc[key] = {
            key,
            title: detail.description,
            subtitle: this.getFindingActionDeviceSubtitle(detail),
            findings: [],
          }
        }

        const findings = detail.task_checklist_findings || []

        findings.forEach((finding) => {
          acc[key].findings.push({
            id: `${detail.tcd_id}-${finding.checklist_finding_id}`,
            name: finding.checklist_finding_name || '-',
            finding_note: finding.finding_note,
            actions: finding.corrective_action
          })
        })

        return acc
      }, {})

      return Object.values(grouped)
    },

    findingActionSummary() {
      const findings = this.findingActionGroups.reduce(
        (total, group) => total + group.findings.length,
        0
      )
      const actions = this.findingActionGroups.reduce((total, group) => {
        return (
          total +
          group.findings.reduce((findingTotal, finding) => findingTotal + finding.actions.length, 0)
        )
      }, 0)

      return {
        findings,
        actions,
        devices: this.findingActionGroups.length,
      }
    },
  },

  methods: {
    getFindingActionDeviceSubtitle(detail) {
      const parts = [detail.description]

      if (detail.reserved_product_id) {
        parts.push(`Product ID ${detail.reserved_product_id}`)
      }

      if (detail.component_line_id) {
        parts.push(`Line ID ${detail.component_line_id}`)
      }

      return parts.filter(Boolean).join(' - ')
    },
  },
}
</script>

<template>
  <div class="finding-action-view">
    <div class="finding-action-summary">
      <div class="finding-action-summary__item finding-action-summary__item--danger">
        <strong>{{ findingActionSummary.findings }}</strong>
        <span>temuan</span>
      </div>
      <div class="finding-action-summary__item finding-action-summary__item--primary">
        <strong>{{ findingActionSummary.actions }}</strong>
        <span>tindakan tercatat</span>
      </div>
      <div class="finding-action-summary__item finding-action-summary__item--primary">
        <strong>{{ findingActionSummary.devices }}</strong>
        <span>device terpasang</span>
      </div>
    </div>

    <div v-for="group in findingActionGroups" :key="group.key" class="finding-action-device">
      <div class="finding-action-device__header">
        <div>
          <h6>{{ group.title }}</h6>
          <p>{{ group.subtitle }}</p>
        </div>
        <span class="finding-action-device__badge"> {{ group.findings.length }} temuan </span>
      </div>

      <div
        v-for="(finding, findingIndex) in group.findings"
        :key="finding.id"
        class="finding-action-card"
      >
        <div class="finding-action-card__body">
          <span class="finding-action-card__number">{{ findingIndex + 1 }}</span>
          <div>
            <div class="finding-action-card__label">TEMUAN</div>
            <div class="d-flex gap-3">
              <h6>{{ finding.name }}</h6>
              <h6 v-if="finding.finding_note">- {{ finding.finding_note }}</h6>
            </div>
          </div>
        </div>

        <div class="finding-action-card__actions">
          <div class="finding-action-card__action-title">
            TINDAKAN
            <span>{{ finding.actions.length }} dipilih</span>
          </div>
          <div
            v-for="(action, actionIndex) in finding.actions"
            :key="`${finding.id}-action-${actionIndex}`"
            class="finding-action-card__action"
          >
            <i class="fa fa-check"></i>
            <div class="d-flex gap-3">
              <span>{{ action.corrective_action_name }}</span>
              <span v-if="action.action_note">- {{ action.action_note }}</span>
            </div>
          </div>
          <div v-if="!finding.actions.length" class="finding-action-card__empty">
            {{ $t('global.emptyTable') }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="!findingActionGroups.length" class="text-center text-muted">
      {{ $t('global.emptyTable') }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.finding-action-view {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 560px;
  overflow-y: auto;
  padding: 12px;
}

.finding-action-summary {
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 18px;
  margin: -12px -12px 12px;
  padding: 12px 16px;
}

.finding-action-summary__item {
  align-items: baseline;
  border-right: 1px solid #eef2f7;
  display: flex;
  gap: 6px;
  padding-right: 18px;

  &:last-child {
    border-right: 0;
  }

  strong {
    font-size: 18px;
    line-height: 1;
  }

  span {
    color: #8a98aa;
    font-size: 13px;
  }
}

.finding-action-summary__item--danger strong {
  color: #dc2626;
}

.finding-action-summary__item--primary strong {
  color: #1f6bc1;
}

.finding-action-device {
  background: #fff;
  border: 1px solid #e7edf5;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.finding-action-device__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;

  h6 {
    color: #0f172a;
    font-size: 14px;
    font-weight: 700;
    margin: 0;
  }

  p {
    color: #8a98aa;
    font-size: 12px;
    margin: 3px 0 0;
  }
}

.finding-action-device__badge {
  background: #fff3df;
  border-radius: 999px;
  color: #ea7500;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 10px;
}

.finding-action-card {
  background: #fff7f6;
  border-left: 3px solid #ef3b2d;
  margin: 0 10px 10px;
}

.finding-action-card__body {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  padding: 12px 12px 10px;

  h6 {
    color: #111827;
    font-size: 14px;
    font-weight: 700;
    margin: 0;
  }
}

.finding-action-card__number {
  align-items: center;
  background: #e52e25;
  border-radius: 50%;
  color: #fff;
  display: inline-flex;
  flex: 0 0 18px;
  font-size: 11px;
  font-weight: 700;
  height: 18px;
  justify-content: center;
  line-height: 1;
  margin-top: 1px;
  width: 18px;
}

.finding-action-card__label,
.finding-action-card__action-title {
  color: #1266bd;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.2;
}

.finding-action-card__label {
  color: #e52e25;
  margin-bottom: 4px;
}

.finding-action-card__actions {
  background: #fff;
  border-top: 1px solid #eef2f7;
  padding: 8px 10px 10px 40px;
}

.finding-action-card__action-title {
  margin-bottom: 6px;

  span {
    background: #eef6ff;
    border-radius: 999px;
    margin-left: 6px;
    padding: 1px 7px;
  }
}

.finding-action-card__action {
  align-items: center;
  background: #f0f6ff;
  border-radius: 6px;
  color: #1f6bc1;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  gap: 9px;
  margin-top: 6px;
  min-height: 30px;
  padding: 6px 10px;

  i {
    font-size: 11px;
  }
}

.finding-action-card__empty {
  color: #8a98aa;
  font-size: 13px;
  padding: 4px 0;
}
</style>
